import React, {
  CSSProperties,
  FunctionComponent,
  SyntheticEvent,
  useCallback,
  useEffect,
} from "react"
import ReactFlow, {
  Background,
  Controls,
  EdgeTypes,
  NodeChange,
  NodeTypes,
  Viewport,
  useOnSelectionChange,
  useOnViewportChange,
  useReactFlow,
  useStoreApi,
} from "reactflow"
import "reactflow/dist/style.css"
import { DeterministicNode } from "./node/DeterministicNode"
import { ContinuousNode } from "./node/ContinuousNode"
import { CategoricalNode } from "./node/CategoricalNode"
import { DiscreteNode } from "./node/DiscreteNode"
import { MixtureNode } from "./node/MixtureNode"
import { GroupNode } from "./node/GroupNode"
import {
  GraphDataViewModel,
  GraphEditMode,
  GraphNodeSelection,
  GraphSelectionModel,
} from "./graphViewModel"
import { GraphBaseEdge, GraphOtherEdge } from "./edge/GraphBaseEdge"
import { graphApplyEdgeChanges, graphApplyNodeChanges } from "./changes/graphApplyChanges"
import { TableNode } from "./node/TableNode"
import { graphNodeClickHandler } from "@features/graph/handlers/graphNodeClick"
import { graphNodeChange } from "@features/graph/handlers/graphNodeChange"
import { graphNodeIdFromEvent } from "@features/graph/utils/graphNodeUtils"
import { GraphConverter } from "@store/services/converter/graphConverter"
import { GraphNodeEdgeData } from "@store/services/converter/types"
import { GraphDto, UserGraphDto } from "@custom-types/graphs"
import { deepCopy, graphFocusOnNode } from "@features/graph/utils/graphUtils"
import { LagNode } from "@features/graph/node/LagNode"
import { ScenarioNode } from "@features/graph/node/ScenarioNode"
import { ForecastNode } from "@features/graph/node/ForecastNode"
import { ParentChildHistory } from "@features/graph/state/parentChildHistory"
import { useSetNodeSelection } from "@features/graph/state/graphAtoms"
import { useSearchParams } from "react-router"
import { ZoomInButton } from "@features/graph/controls/ZoomInButton"
import { ZoomOutButton } from "@features/graph/controls/ZoomOutButton"
import { FitViewButton } from "@features/graph/controls/FitViewButton"
import { ActiveShortcutsInfoButton } from "@features/graph/controls/ActiveShortcutsInfoButton"
import logger from "@utils/logger"

const defaultViewport = { x: 0, y: 0, zoom: 1.5 }

interface Props {
  graph: UserGraphDto | GraphDto
  editMode: GraphEditMode
  selectParentChild?: boolean
  skipChipsSelection?: boolean
  graphData?: GraphNodeEdgeData
  viewPort?: Viewport
  style?: CSSProperties | undefined
  onViewPortChange?: (viewPort: Viewport) => void
  selectionChangeCallback?: (selection: GraphNodeSelection) => void
}

export type GraphStoreType = ReturnType<typeof useStoreApi>

const nodeTypes: NodeTypes = {
  deterministic: DeterministicNode,
  continuous: ContinuousNode,
  categorical: CategoricalNode,
  discrete: DiscreteNode,
  mixture: MixtureNode,
  group: GroupNode,
  table: TableNode,
  // pseudo nodes
  lag: LagNode,
  scenario: ScenarioNode,
  forecast: ForecastNode,
}

const edgeTypes: EdgeTypes = {
  // @ts-ignore
  custom_base: GraphBaseEdge,
  custom_base_other: GraphOtherEdge,
}

export const Graph: FunctionComponent<Props> = ({
  graphData,
  selectionChangeCallback,
  viewPort,
  onViewPortChange,
  editMode,
  selectParentChild,
  skipChipsSelection,
  style,
}) => {
  const reactFlowInstance = useReactFlow()
  const { fitView, setViewport } = reactFlowInstance
  const { addNode } = useSetNodeSelection()
  const [searchParams] = useSearchParams()
  useOnViewportChange({
    onEnd: (v) => {
      if (onViewPortChange) {
        onViewPortChange(v)
      }
    },
  })
  const graphStore: GraphStoreType = useStoreApi()

  useEffect(() => {
    if (!graphData) {
      return
    }

    GraphSelectionModel.clear()
    const s = graphStore.getState()
    GraphDataViewModel.setGraphData(graphData)
    s.setNodes(deepCopy(graphData.nodes))
    s.setEdges(deepCopy(graphData.edges))
    if (viewPort) {
      setTimeout(() => setViewport({ ...viewPort }, { duration: 0 }), 0)
    } else {
      setTimeout(() => fitView(), 0)
    }
    applySelection()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [graphData])

  const applySelection = () => {
    if (!graphData) {
      return
    }
    const labels = searchParams.getAll("nodeLabel")
    if (labels.length == 0) {
      return
    }
    const entries = Object.entries(graphData.labelMap)
    if (skipChipsSelection) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      graphFocusOnNode(labels[0], graphData, graphStore)
    } else {
      labels.forEach((n) => {
        const nodeId = entries.filter(([_, value]) => value === n)

        if (!nodeId[0]?.[0]) {
          logger.error("Corresponding node not found")
          return
        }

        const nodeData = graphData.nodeMap[nodeId[0][0]]

        if (!nodeData) {
          logger.error("Corresponding node data not found")
          return
        }

        const node = GraphConverter.createNodeFromNodeData(nodeData)
        addNode(node)
      })
    }
  }

  useOnSelectionChange({
    onChange: ({ nodes }) => {
      if (!selectionChangeCallback) {
        return
      }
      // we want to pass only those selected that are not marked as parent or child
      const filtered = nodes.filter(
        (n) => !GraphSelectionModel.parent.has(n.id) && !GraphSelectionModel.child.has(n.id),
      )
      if (nodes.length === 0 && GraphSelectionModel.selected) {
        const { nodeChanges } = GraphSelectionModel.clearSelectionChanges()
        GraphSelectionModel.clear()
        const { nodeInternals, edges, defaultEdgeOptions } = graphStore.getState()
        graphStore.setState({
          nodeInternals: graphApplyNodeChanges(nodeChanges, nodeInternals),
          edges: graphApplyEdgeChanges(edges, defaultEdgeOptions),
        })
      }

      const isExpandingIconClicked = GraphDataViewModel.isExpandingIconClicked
      // ignore selection on icon
      if (GraphDataViewModel.isExpandingIconClicked) {
        GraphDataViewModel.isExpandingIconClicked = false
      }
      selectionChangeCallback({
        nodes: filtered,
        isExpandingIconClicked: isExpandingIconClicked,
      })
    },
  })

  const handleNodeClick = useCallback((e: SyntheticEvent<Element, MouseEvent>) => {
    const id = graphNodeIdFromEvent(e)
    if (!id) {
      return
    }
    graphNodeClickHandler(id, graphStore)
    ParentChildHistory.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  const handleNodeChange = (changes: NodeChange[]) => {
    const { edges, nodeInternals } = graphStore.getState()
    const { nodeChanges, newNodes } = graphNodeChange(
      changes,
      nodeInternals,
      edges,
      editMode,
      selectParentChild,
    )
    // nodes
    if (!newNodes) {
      return
    }
    graphStore.setState({
      nodeInternals: graphApplyNodeChanges(nodeChanges, nodeInternals),
      edges,
    })
  }

  const viewState = graphStore.getState()

  return (
    <>
      <ReactFlow
        multiSelectionKeyCode={null} // disable multiSelection
        nodes={viewState.getNodes()}
        edges={viewState.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={handleNodeClick}
        onNodesChange={handleNodeChange}
        defaultViewport={defaultViewport}
        edgesUpdatable={editMode !== GraphEditMode.OFF}
        edgesFocusable={editMode !== GraphEditMode.OFF}
        nodesDraggable={editMode === GraphEditMode.BUILD} // nodesDraggable triggers useOnSelectionChange before onClick
        nodesFocusable={editMode !== GraphEditMode.OFF}
        zoomOnDoubleClick={false}
        nodesConnectable={editMode !== GraphEditMode.OFF}
        // has to be disabled, otherwise it'll keep changing zIndex (groups in groups)
        elevateEdgesOnSelect={false}
        elevateNodesOnSelect={false}
        fitView
        selectionOnDrag={false}
        snapGrid={[1, 1]}
        snapToGrid={true}
        maxZoom={7}
        minZoom={0.01} // allows to zoom out whole graph see https://github.com/xyflow/xyflow/discussions/3160
        proOptions={{ hideAttribution: true }}
        className="graph-editor"
        style={style}
      >
        <Controls
          position="top-left"
          showZoom={false}
          showFitView={false}
          showInteractive={editMode !== GraphEditMode.OFF}
        >
          <ZoomInButton />
          <ZoomOutButton />
          <FitViewButton />
          <ActiveShortcutsInfoButton />
        </Controls>
        <Background color="#ccc" />
      </ReactFlow>
    </>
  )
}
